import isFunction from "lodash/isFunction"
import get from "lodash/get"
import { getFlattenColumns, getValue } from "./utils"

const sortMap = new Map([
  [true, "asc"],
  [false, "desc"],
  ["asc", true],
  ["desc", false],
  ["ASC", true],
  ["DESC", false],
])

class TableStore {
  constructor(init) {
    this.state = {
      flattenColumns: [],
      columns: [],
      rows: [],
      sortedRows: [],
      filteredRows: [],
      enabledRows: [],
      selectedRows: [],
      selectAll: false,
      indeterminate: false,
      dragRow: null,
      dragRowIndex: -1,
      rowKey: "",
      init: false,
    }

    this.layout = {
      headerHeight: 0,
      headerRowHeightList: [],
      containerWidth: 0,
      tableWidth: 0,
      rowHeightList: [],
      hoverRowIndex: -1,
      scrollBarWidth: 2,
      scrollX: 0,
      scrollY: 0,
      canScrollX: true,
      canScrollY: true,
      dragoverRowIndex: -1,
      dragDirection: 1,
      selectColWidth: 0,
      pageHeight: 0,
      footerHeight: 0,
    }

    this.refs = {
      thead: null,
      tbody: null,
      fixedTbody: null,
      rightFixedTbody: null,
    }

    this.operations = {
      continueSort: true,
      rowSelection: null,
      sortColumn: null,
      sortOrder: true, // true for ascending, false for desc
      filters: {},
    }

    for (let key in init) {
      if (init.hasOwnProperty(key) && this.state.hasOwnProperty(key)) {
        this.state[key] = init[key]
      }
    }
  }

  setState(key, value) {
    this.state[key] = value

    switch (key) {
      case "columns":
        this.setDefaultSort()
        break
      case "rows":
        this.doSort()
        break
    }
  }

  setLayout(key, value) {
    this.layout[key] = value
  }

  setRowSelection(value) {
    this.operations.rowSelection = value
  }

  setFilter(value) {
    const { filter, keywords, onFilter } = value
    this.operations.filters[filter] = {
      keywords,
      onFilter,
    }

    this.doFilters()
  }

  doFilters() {
    const { sortedRows, rowKey, selectedRows } = this.state
    let { rowSelection, filters } = this.operations
    const getCheckboxProps = rowSelection && rowSelection.getCheckboxProps

    this.state.filteredRows = getFilteredRows(sortedRows, filters)
    this.state.enabledRows = getCheckboxProps
      ? this.state.filteredRows.filter(row => !getCheckboxProps(row).disabled)
      : this.state.filteredRows

    const selectedRowsCopy = rowKey
      ? sortedRows.filter(item => selectedRows.includes(get(item, rowKey)))
      : selectedRows
    const filteredSelectedRows = getFilteredRows(selectedRowsCopy, filters)
    this.state.selectedRows = rowKey
      ? filteredSelectedRows.map(item => get(item, rowKey))
      : filteredSelectedRows
  }

  selectAllHandler(value) {
    const { rowKey } = this.state
    this.state.selectedRows = value
      ? rowKey
        ? this.state.enabledRows.map(item => get(item, rowKey))
        : this.state.enabledRows
      : []
  }

  setScrollStatus(x, y) {
    this.layout.canScrollX = x
    this.layout.canScrollY = y
  }

  setScroll(value, payload) {
    this.layout[`scroll${payload.toUpperCase()}`] = value
  }

  setRef(key, value) {
    this.refs[key] = value
  }

  setDefaultSort() {
    if (!this.state.init && this.state.columns.length) {
      const column = getFlattenColumns(this.state.columns).find(column => column.defaultSort)
      if (column) {
        this.operations.sortColumn = column
        this.operations.sortOrder = sortMap.get(column.defaultSort)
      }
      this.state.init = true
    }

    // 更新 sortColumn 引用
    if (this.state.init && this.operations.sortColumn) {
      const { key } = this.operations.sortColumn
      this.operations.sortColumn = getFlattenColumns(this.state.columns).find(
        column => column.key === key
      )
    }
  }

  getSortAlias(order) {
    return sortMap.get(order)
  }

  setSort(column, order) {
    let { sortColumn, sortOrder } = this.operations

    if (sortColumn === column && sortOrder === order) {
      sortColumn = null
      sortOrder = null
    } else {
      sortColumn = column
      sortOrder = order
    }

    this.operations.sortColumn = sortColumn
    this.operations.sortOrder = sortOrder

    if (isFunction(column.onBeforeSort)) {
      this.operations.continueSort = column.onBeforeSort({
        column,
        order: sortOrder === null ? undefined : sortMap.get(sortOrder),
      })
    }

    if ([true, undefined].includes(this.operations.continueSort)) {
      this.doSort()
    }
  }

  doSort() {
    const { sortColumn, sortOrder, continueSort } = this.operations
    if (sortColumn && sortColumn.sorter !== true && continueSort) {
      const sorter = isFunction(sortColumn.sorter)
        ? sortColumn.sorter
        : (a, b) => getValue({ row: a, column: sortColumn }) - getValue({ row: b, column: sortColumn })
      const sorted = [...this.state.rows].sort(sorter)
      this.state.sortedRows = sortOrder ? sorted : sorted.reverse()
    } else {
      this.state.sortedRows = [...this.state.rows]
    }
    this.doFilters()
  }

  setDragRow(row) {
    this.state.dragRow = row
    this.state.dragRowIndex = this.state.rows.findIndex(item => item === row)
  }

  setDragoverIndex(row, rowIndex) {
    const index = this.state.rows.findIndex(item => item === row)
    this.layout.dragoverRowIndex = rowIndex
    this.layout.dragDirection = index > this.state.dragRowIndex ? 1 : 0
  }

  setDrop(row, callback) {
    const index = this.state.rows.findIndex(item => item === row)
    this.state.rows.splice(this.state.dragRowIndex, 1)
    this.state.rows.splice(index, 0, this.state.dragRow)

    callback(index, this.state.dragRowIndex)

    this.setDragleave()
  }

  setDragleave() {
    this.state.dragRow = null
    this.state.dragRowIndex = -1
    this.layout.dragoverRowIndex = -1
  }
}

const getFilteredRows = function(rows, filters) {
  let result = rows

  for (const key in filters) {
    if (filters.hasOwnProperty(key)) {
      const { keywords, onFilter } = filters[key]
      result = keywords.length
        ? result.filter(row => keywords.some(keyword => onFilter(keyword, row)))
        : result
    }
  }

  return result
}

export default TableStore
